import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  MagnifyingGlass, ShareNetwork, TwitterLogo, ChartLineUp,
  ShieldCheck, Code, Image as ImageIcon, MapPin, ArrowRight,
  ArrowSquareOut, Robot,
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Section = ({ icon: Icon, title, description, color = '#0052FF', children, testid }) => (
  <Card className="dashboard-card" data-testid={testid}>
    <CardHeader className="flex flex-row items-start gap-3 space-y-0">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}15`, color }}
      >
        <Icon size={20} weight="fill" />
      </div>
      <div className="flex-1">
        <CardTitle className="text-[16px]">{title}</CardTitle>
        {description && <CardDescription className="mt-1">{description}</CardDescription>}
      </div>
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const SettingsPage = () => {
  const { currentStore, updateStoreSettings } = useStore();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const ogImageRef = useRef(null);
  const twitterImageRef = useRef(null);

  useEffect(() => {
    if (!currentStore) return;
    axios.get(`${API_URL}/api/stores/${currentStore.id}`, { withCredentials: true })
      .then(resp => {
        if (resp.data?.settings) setSettings(resp.data.settings);
      })
      .catch(() => toast.error('Impossible de charger les paramètres'));
  }, [currentStore]);

  const set = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateStoreSettings(currentStore.id, settings);
      toast.success('Paramètres SEO & Tracking mis à jour');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally { setLoading(false); }
  };

  const uploadImage = async (file, field) => {
    if (!file || !currentStore) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('seo_name', field);
    fd.append('quality', 'high');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/stores/${currentStore.id}/media/upload`,
        fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const url = data.url || data.path;
      set(field, url);
      toast.success('Image uploadée');
    } catch (e) {
      toast.error("Échec de l'upload de l'image");
    }
  };

  if (!currentStore) return null;

  const feedLinks = [
    { label: 'Sitemap XML', path: '/api/sitemap.xml', testid: 'copy-sitemap', desc: 'À soumettre dans Google Search Console.' },
    { label: 'Robots.txt', path: '/api/robots.txt', testid: 'copy-robots', desc: 'Indique aux moteurs ce qu\'ils peuvent explorer.' },
    { label: 'Flux Google Merchant Center', path: '/api/merchant-feed.xml', testid: 'copy-merchant', desc: 'À ajouter dans Merchant Center pour Google Shopping (gratuit).' },
  ];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight" style={{ color: '#0f172a' }}>SEO & Tracking</h1>
        <p className="text-slate-500 mt-1">Optimisez votre référencement, vos aperçus sociaux et vos trackers publicitaires.</p>
      </div>

      {/* 1. Metadata SEO */}
      <Section
        icon={MagnifyingGlass}
        title="Métadonnées de référencement"
        description="Le titre et la description qui apparaissent dans les résultats Google."
        color="#0052FF"
        testid="section-meta"
      >
        <div className="space-y-2">
          <Label>Titre de la page (meta title)</Label>
          <Input
            value={settings.meta_title || ''}
            onChange={(e) => set('meta_title', e.target.value)}
            placeholder="MAGERE Viandes — Boucherie artisanale halal à Rueil-Malmaison"
            data-testid="meta-title"
          />
          <p className="text-xs text-slate-500">{settings.meta_title?.length || 0}/60 caractères recommandés</p>
        </div>
        <div className="space-y-2">
          <Label>Description (meta description)</Label>
          <Textarea
            value={settings.meta_description || ''}
            onChange={(e) => set('meta_description', e.target.value)}
            rows={3}
            placeholder="Découvrez notre boucherie artisanale à Rueil-Malmaison — viande halal, colis famille, livraison offerte dès 100 €."
            data-testid="meta-description"
          />
          <p className="text-xs text-slate-500">{settings.meta_description?.length || 0}/160 caractères recommandés</p>
        </div>
        <div className="space-y-2">
          <Label>Mots-clés SEO globaux</Label>
          <Input
            value={settings.meta_keywords || ''}
            onChange={(e) => set('meta_keywords', e.target.value)}
            placeholder="boucherie rueil-malmaison, viande halal 92500, colis famille"
            data-testid="meta-keywords"
          />
          <p className="text-xs text-slate-500">Séparés par des virgules. Utilisés dans certains moteurs et en analyse concurrentielle.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><MapPin size={14} /> Zone géographique cible</Label>
            <Input
              value={settings.geo_focus || ''}
              onChange={(e) => set('geo_focus', e.target.value)}
              placeholder="Rueil-Malmaison, Hauts-de-Seine, 92500"
              data-testid="geo-focus"
            />
          </div>
          <div className="space-y-2">
            <Label>URL canonique de base</Label>
            <Input
              value={settings.canonical_base || ''}
              onChange={(e) => set('canonical_base', e.target.value)}
              placeholder="https://magere-viandes.fr"
              data-testid="canonical-base"
            />
          </div>
        </div>

        {/* Google preview */}
        <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
          <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Aperçu Google</p>
          <p className="text-[#1a0dab] text-lg hover:underline cursor-pointer">
            {settings.meta_title || 'Titre de votre boutique'}
          </p>
          <p className="text-[#006621] text-sm">{(settings.canonical_base || 'https://magere-viandes.fr').replace(/^https?:\/\//, '')}</p>
          <p className="text-sm text-slate-600 mt-0.5">
            {settings.meta_description || 'Description de votre boutique qui apparaîtra dans les résultats de recherche.'}
          </p>
        </div>
      </Section>

      {/* 2. Open Graph */}
      <Section
        icon={ShareNetwork}
        title="Aperçu Open Graph (Facebook, LinkedIn, WhatsApp)"
        description="Ce qui s'affiche quand un lien vers votre site est partagé sur les réseaux sociaux."
        color="#1877F2"
        testid="section-og"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Titre OG</Label>
            <Input
              value={settings.og_title || ''}
              onChange={(e) => set('og_title', e.target.value)}
              placeholder="Ex: MAGERE Viandes — Boucherie halal artisanale"
              data-testid="og-title"
            />
          </div>
          <div className="space-y-2">
            <Label>Description OG</Label>
            <Input
              value={settings.og_description || ''}
              onChange={(e) => set('og_description', e.target.value)}
              placeholder="Livraison à domicile en Île-de-France"
              data-testid="og-description"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><ImageIcon size={14} /> Image de partage (recommandé 1200×630)</Label>
          <div className="flex items-start gap-3">
            {settings.og_image_url && (
              <img src={settings.og_image_url} alt="OG" className="w-32 h-20 object-cover rounded-lg border border-slate-200" data-testid="og-image-preview" />
            )}
            <div className="flex-1 space-y-2">
              <input
                ref={ogImageRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadImage(e.target.files?.[0], 'og_image_url')}
                data-testid="og-image-input"
              />
              <Button variant="outline" onClick={() => ogImageRef.current?.click()} data-testid="og-image-upload-btn">
                <ImageIcon size={14} className="mr-1.5" />
                {settings.og_image_url ? 'Remplacer l\'image' : 'Uploader une image'}
              </Button>
              {settings.og_image_url && (
                <Button variant="ghost" size="sm" onClick={() => set('og_image_url', '')} className="text-red-600" data-testid="og-image-remove-btn">
                  Supprimer
                </Button>
              )}
              <p className="text-xs text-slate-500">Utilisée par Facebook, LinkedIn, WhatsApp, iMessage.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* 3. Twitter Card */}
      <Section
        icon={TwitterLogo}
        title="Aperçu Twitter / X"
        description="Personnalisez la Twitter Card affichée lors d'un partage sur X."
        color="#000000"
        testid="section-twitter"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Type de card</Label>
            <select
              value={settings.twitter_card || 'summary_large_image'}
              onChange={(e) => set('twitter_card', e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
              data-testid="twitter-card-type"
            >
              <option value="summary">Résumé (petite image)</option>
              <option value="summary_large_image">Résumé grande image (recommandé)</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Handle Twitter (@compte)</Label>
            <Input
              value={settings.twitter_handle || ''}
              onChange={(e) => set('twitter_handle', e.target.value)}
              placeholder="@magere_viandes"
              data-testid="twitter-handle"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Image Twitter (peut différer de l'image OG)</Label>
          <div className="flex items-start gap-3">
            {settings.twitter_image_url && (
              <img src={settings.twitter_image_url} alt="Twitter" className="w-32 h-20 object-cover rounded-lg border border-slate-200" data-testid="twitter-image-preview" />
            )}
            <div className="flex-1 space-y-2">
              <input
                ref={twitterImageRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadImage(e.target.files?.[0], 'twitter_image_url')}
                data-testid="twitter-image-input"
              />
              <Button variant="outline" onClick={() => twitterImageRef.current?.click()} data-testid="twitter-image-upload-btn">
                <ImageIcon size={14} className="mr-1.5" />
                {settings.twitter_image_url ? 'Remplacer l\'image' : 'Uploader une image'}
              </Button>
              <p className="text-xs text-slate-500">Par défaut, l'image OG est utilisée si celle-ci n'est pas définie.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* 4. Verifications */}
      <Section
        icon={ShieldCheck}
        title="Vérifications propriétaire"
        description="Prouvez la propriété du domaine auprès des moteurs et plateformes."
        color="#059669"
        testid="section-verifications"
      >
        <div className="space-y-2">
          <Label>Google Search Console (meta content)</Label>
          <Input
            value={settings.gsc_verification || ''}
            onChange={(e) => set('gsc_verification', e.target.value)}
            placeholder="Ex: AbCdEfGhIjKlMnOp..."
            data-testid="gsc-verification"
          />
          <p className="text-xs text-slate-500">Récupérable dans <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Search Console</a> → Ajouter une propriété → Balise HTML.</p>
        </div>
        <div className="space-y-2">
          <Label>Bing Webmaster Tools</Label>
          <Input
            value={settings.bing_verification || ''}
            onChange={(e) => set('bing_verification', e.target.value)}
            placeholder="Ex: 1A2B3C4D5E6F..."
            data-testid="bing-verification"
          />
        </div>
        <div className="space-y-2">
          <Label>Facebook Business (Domain verification)</Label>
          <Input
            value={settings.fb_domain_verification || ''}
            onChange={(e) => set('fb_domain_verification', e.target.value)}
            placeholder="Ex: abcd1234efgh5678..."
            data-testid="fb-domain-verification"
          />
        </div>
      </Section>

      {/* 5. Trackers */}
      <Section
        icon={ChartLineUp}
        title="Trackers & Analytics"
        description="Identifiants de tracking pour la mesure d'audience et de conversions."
        color="#7C3AED"
        testid="section-trackers"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Google Analytics 4</Label>
            <Input
              value={settings.google_analytics_id || ''}
              onChange={(e) => set('google_analytics_id', e.target.value)}
              placeholder="G-XXXXXXXXXX"
              data-testid="ga-id"
            />
          </div>
          <div className="space-y-2">
            <Label>Google Tag Manager</Label>
            <Input
              value={settings.gtm_id || ''}
              onChange={(e) => set('gtm_id', e.target.value)}
              placeholder="GTM-XXXXXXX"
              data-testid="gtm-id"
            />
          </div>
          <div className="space-y-2">
            <Label>Facebook Pixel ID</Label>
            <Input
              value={settings.fb_pixel_id || ''}
              onChange={(e) => set('fb_pixel_id', e.target.value)}
              placeholder="123456789012345"
              data-testid="fb-pixel-id"
            />
          </div>
          <div className="space-y-2">
            <Label>Google Ads Conversion</Label>
            <Input
              value={settings.gads_conversion_id || ''}
              onChange={(e) => set('gads_conversion_id', e.target.value)}
              placeholder="AW-1234567890"
              data-testid="gads-conversion-id"
            />
          </div>
        </div>
        <div className="space-y-2 pt-2 border-t border-slate-100">
          <Label>URL Avis Google (lien direct de dépôt)</Label>
          <Input
            value={settings.google_review_url || ''}
            onChange={(e) => set('google_review_url', e.target.value)}
            placeholder="https://g.page/r/XXXXXXX/review"
            data-testid="google-review-url"
          />
          <p className="text-xs text-slate-500">Utilisé par le bouton "Demander un avis" sur les commandes livrées.</p>
        </div>
      </Section>

      {/* 6. Structured Data */}
      <Section
        icon={Code}
        title="Données structurées (JSON-LD)"
        description="Améliorent votre référencement en fournissant des informations structurées aux moteurs."
        color="#F59E0B"
        testid="section-jsonld"
      >
        <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Activer JSON-LD (Organization + LocalBusiness + Products)</p>
            <p className="text-xs text-slate-600 mt-0.5">
              Alimenté automatiquement depuis <Link to="/admin/company" className="text-blue-600 underline">/admin/company</Link> (raison sociale, coordonnées, horaires, réseaux) et vos produits (prix, dispo, TVA).
            </p>
          </div>
          <Switch
            checked={settings.jsonld_enabled ?? true}
            onCheckedChange={(v) => set('jsonld_enabled', v)}
            data-testid="jsonld-enabled"
          />
        </div>
      </Section>

      {/* 7. Robots & Canonical */}
      <Section
        icon={Robot}
        title="Robots & indexation"
        description="Contrôlez ce que les moteurs peuvent indexer sur votre site."
        color="#64748B"
        testid="section-robots"
      >
        <div className="space-y-2">
          <Label>Directive robots par défaut</Label>
          <select
            value={settings.default_robots || 'index,follow'}
            onChange={(e) => set('default_robots', e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
            data-testid="default-robots"
          >
            <option value="index,follow">index, follow (par défaut — recommandé)</option>
            <option value="index,nofollow">index, nofollow</option>
            <option value="noindex,follow">noindex, follow</option>
            <option value="noindex,nofollow">noindex, nofollow (site invisible sur Google)</option>
          </select>
          <p className="text-xs text-slate-500">
            ⚠️ "noindex" retire votre site de Google. À utiliser uniquement en développement.
          </p>
        </div>
      </Section>

      {/* 8. SEO Feeds */}
      <Section
        icon={ArrowSquareOut}
        title="Flux SEO & Google Shopping"
        description="URLs à soumettre dans les outils Google/Bing."
        color="#0284C7"
        testid="section-feeds"
      >
        {feedLinks.map((item) => {
          const fullUrl = `${window.location.origin}${item.path}`;
          return (
            <div key={item.path} className="flex flex-col gap-2 p-3 bg-slate-50 rounded-md">
              <div className="flex items-center justify-between gap-2">
                <Label className="font-medium">{item.label}</Label>
                <Button
                  type="button" variant="outline" size="sm"
                  data-testid={item.testid}
                  onClick={() => { navigator.clipboard.writeText(fullUrl); toast.success(`${item.label} copié !`); }}
                >
                  Copier l'URL
                </Button>
              </div>
              <code className="text-xs text-slate-700 break-all">{fullUrl}</code>
              <p className="text-xs text-slate-500">{item.desc}</p>
            </div>
          );
        })}
        <div className="flex flex-wrap gap-4 pt-2">
          <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="link-gsc">
            → Google Search Console
          </a>
          <a href="https://merchants.google.com/" target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="link-merchant-center">
            → Google Merchant Center
          </a>
          <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="link-bing-webmaster">
            → Bing Webmaster Tools
          </a>
        </div>
      </Section>

      {/* 9. Rank tracking teaser */}
      <Card className="dashboard-card bg-gradient-to-br from-purple-50 to-white border-purple-200" data-testid="section-rank">
        <CardContent className="p-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
              <ChartLineUp size={20} weight="fill" />
            </div>
            <div>
              <p className="font-bold text-slate-800">Suivi de positionnement</p>
              <p className="text-sm text-slate-600">Suivez vos mots-clés sur Google et vos concurrents dans le temps.</p>
            </div>
          </div>
          <Link to="/admin/seo-rank">
            <Button variant="outline" data-testid="open-seo-rank-btn">
              Ouvrir <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* Sticky Save */}
      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={loading}
          size="lg"
          className="shadow-xl bg-[#0052FF] hover:bg-[#0040CC] rounded-xl"
          data-testid="save-seo"
        >
          {loading ? 'Sauvegarde...' : 'Sauvegarder tous les paramètres SEO & Tracking'}
        </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
